import { z } from 'zod';
import { sequenceBranchRuleSchema } from '../../sequences/branchRules.js';
import { segmentRuleSchema } from '../../schemas/segmentDefinition.js';
export const COPILOT_PLAN_TYPES = [
    'AUDIENCE_CREATION',
    'CAMPAIGN_CREATION',
    'CAMPAIGN_OPTIMISATION',
    'MARKETING_STRATEGY',
];
export const COPILOT_PLAN_STATUSES = [
    'collecting_information',
    'planning',
    'ready_for_review',
    'needs_revision',
    'completed',
    'cancelled',
];
export const ACTIVE_PLAN_STATUSES = [
    'collecting_information',
    'planning',
    'ready_for_review',
    'needs_revision',
];
export const missingFieldSchema = z.object({
    key: z.string().min(1).max(120),
    label: z.string().min(1).max(200),
    reason: z.string().min(1).max(500),
    required: z.boolean(),
    priority: z.enum(['blocking', 'important', 'optional']),
    answerType: z.enum(['text', 'number', 'single_select', 'multi_select', 'boolean']),
    options: z.array(z.string()).optional(),
});
export const planFactSchema = z.object({
    key: z.string().min(1).max(120),
    value: z.unknown(),
    source: z.enum(['project', 'integration', 'conversation', 'tool']),
    sourceRef: z.string().max(200).optional(),
    observedAt: z.string().max(40).optional(),
    confidence: z.number().min(0).max(1).optional(),
});
export const planDecisionSchema = z.object({
    key: z.string().min(1).max(120),
    value: z.unknown(),
    source: z.enum(['user', 'confirmed_inference', 'project_fact', 'system_default', 'ai_suggestion']),
    confirmed: z.boolean(),
});
export const planSuggestionSchema = z.object({
    key: z.string().min(1).max(120),
    value: z.unknown(),
    source: z.literal('ai_suggestion'),
    rationale: z.string().max(500).optional(),
});
export const planConflictSchema = z.object({
    code: z.string().min(1).max(80),
    description: z.string().min(1).max(500),
    severity: z.enum(['blocking', 'warning']),
    fields: z.array(z.string()).default([]),
});
export const planWarningSchema = z.object({
    code: z.string().min(1).max(80),
    message: z.string().min(1).max(500),
});
export const planQualityCheckSchema = z.object({
    code: z.string().min(1).max(80),
    message: z.string().min(1).max(500),
    passed: z.boolean(),
});
export const planAssumptionSchema = z.object({
    id: z.string().min(1).max(40),
    statement: z.string().min(1).max(500),
    impact: z.enum(['low', 'medium', 'high']),
    needsConfirmation: z.boolean(),
});
export const planRiskSchema = z.object({
    code: z.string().min(1).max(80),
    description: z.string().min(1).max(500),
    severity: z.enum(['low', 'medium', 'high']),
    mitigation: z.string().max(500).optional(),
});
export const planRecommendationSchema = z.object({
    title: z.string().min(1).max(200),
    detail: z.string().min(1).max(1000),
});
export const marketingObjectiveSchema = z.object({
    primaryGoal: z.enum([
        'lead_generation',
        'appointment_generation',
        'awareness',
        'traffic',
        'conversion',
        'retention',
        'other',
    ]),
    description: z.string().min(1).max(2000),
    target: z.string().max(500).optional(),
    timeframe: z.string().max(200).optional(),
    constraints: z.array(z.string()).optional(),
});
export const audiencePlanSchema = z.object({
    description: z.string().min(1).max(2000),
    industries: z.array(z.string()).optional(),
    locations: z.array(z.string()).optional(),
    companySize: z
        .object({
        min: z.number().int().min(0).optional(),
        max: z.number().int().min(0).optional(),
    })
        .optional(),
    jobRoles: z.array(z.string()).optional(),
    companyTypes: z.array(z.string()).optional(),
    exclusions: z.array(z.string()).optional(),
    existingAudienceId: z.string().uuid().optional(),
    proposedSegmentRules: z.array(segmentRuleSchema).max(16).optional(),
    estimatedSize: z.number().int().min(0).optional(),
    confidence: z.number().min(0).max(1).optional(),
    assumptions: z.array(z.string()).optional(),
});
export const acquisitionPlanSchema = z.object({
    required: z.boolean(),
    method: z.enum(['existing_leads', 'scrape', 'import', 'mixed']).optional(),
    estimatedLeadCount: z.number().int().min(0).optional(),
    sources: z.array(z.string()).optional(),
    filters: z.record(z.string(), z.unknown()).optional(),
    duplicateRisk: z.string().max(500).optional(),
    complianceNotes: z.array(z.string()).optional(),
});
export const campaignPlanSchema = z.object({
    name: z.string().max(200).optional(),
    objective: z.string().min(1).max(2000),
    channel: z.enum(['email', 'multi_channel', 'other']),
    audienceStrategy: z.string().min(1).max(2000),
    positioning: z.string().min(1).max(2000),
    offer: z.string().max(2000).optional(),
    callToAction: z.string().max(500).optional(),
    estimatedVolume: z.number().int().min(0).optional(),
    constraints: z.array(z.string()).optional(),
    complianceNotes: z.array(z.string()).optional(),
    suppressionConsiderations: z.array(z.string()).optional(),
    sendingConstraints: z.array(z.string()).optional(),
});
export const sequencePlanStepSchema = z.object({
    order: z.number().int().min(0),
    type: z.string().min(1).max(80),
    delayDays: z.number().int().min(0).max(365).optional(),
    purpose: z.string().min(1).max(500),
    messagingAngle: z.string().max(500).optional(),
    branchRules: z.array(sequenceBranchRuleSchema).max(10).optional(),
});
export const sequencePlanSchema = z.object({
    steps: z.array(sequencePlanStepSchema).min(1).max(20),
    totalDurationDays: z.number().int().min(0).optional(),
    sendingWindow: z.string().max(200).optional(),
    rationale: z.string().max(2000).optional(),
});
export const messagingAngleSchema = z.object({
    name: z.string().min(1).max(120),
    hook: z.string().min(1).max(500),
});
export const messagingPlanSchema = z.object({
    coreMessage: z.string().min(1).max(2000),
    valueProposition: z.string().min(1).max(2000),
    painPoints: z.array(z.string()).optional(),
    angles: z.array(messagingAngleSchema).optional(),
    tone: z.string().max(200).optional(),
    personalizationIdeas: z.array(z.string()).optional(),
    objections: z.array(z.string()).optional(),
});
export const experimentPlanSchema = z.object({
    hypothesis: z.string().min(1).max(1000),
    variable: z.enum(['subject', 'opening', 'cta', 'sequence_step', 'audience_segment']),
    variants: z.array(z.string()).min(2).max(6),
    primaryMetric: z.string().min(1).max(200),
    minimumSampleSize: z.number().int().min(0).optional(),
});
export const measurementPlanSchema = z.object({
    primaryMetrics: z.array(z.string()).min(1),
    secondaryMetrics: z.array(z.string()).optional(),
    reviewCadence: z.string().max(200).optional(),
});
export const marketingPlanSchema = z.object({
    objective: marketingObjectiveSchema,
    audience: audiencePlanSchema.optional(),
    acquisition: acquisitionPlanSchema.optional(),
    campaign: campaignPlanSchema.optional(),
    sequence: sequencePlanSchema.optional(),
    messaging: messagingPlanSchema.optional(),
    experiments: z.array(experimentPlanSchema).optional(),
    measurements: measurementPlanSchema.optional(),
    assumptions: z.array(planAssumptionSchema).default([]),
    risks: z.array(planRiskSchema).default([]),
    missingInformation: z.array(missingFieldSchema).default([]),
    recommendations: z.array(planRecommendationSchema).default([]),
});
export const knownFieldsSchema = z.object({
    facts: z.array(planFactSchema).default([]),
    decisions: z.array(planDecisionSchema).default([]),
    suggestions: z.array(planSuggestionSchema).default([]),
});
export const planFieldUpdateSchema = z.object({
    path: z.string().min(1).max(200),
    operation: z.enum(['replace', 'add', 'remove']).default('replace'),
    value: z.unknown().optional(),
});
export const proposedPlanFactSchema = planFactSchema;
export const extractedPlanAnswerSchema = z.object({
    fieldKey: z.string().min(1).max(120),
    value: z.unknown(),
    confirmed: z.boolean().default(true),
});
export const planPatchSchema = z.object({
    updates: z.array(planFieldUpdateSchema).default([]),
    extractedAnswers: z.array(extractedPlanAnswerSchema).default([]),
    assumptions: z.array(planAssumptionSchema).optional(),
    reasoningSummary: z.string().max(500).optional(),
});
export const copilotPlanPatchSchema = planPatchSchema.extend({
    factsDiscovered: z.array(proposedPlanFactSchema).optional(),
    conflicts: z.array(planConflictSchema).optional(),
});
//# sourceMappingURL=schemas.js.map